import { markRaw } from 'vue'
import { IStore } from './core/interface/IStore'
import systemAuthStoreV1 from './store'
import { ISysAuthModule } from '@/contracts/system-modules/auth/ISysAuthModule'
import { ISysLockScreenModule } from '@/contracts/system-modules/lock-screen/ISysLockScreenModule'
import { ISysSyncModule } from '@/contracts/system-modules/sync/ISysSyncModule'
import { ISysShareModule } from '@/contracts/system-modules/share/ISysShareModule'
import { ISeedKey } from '@/contracts/system-modules/auth/seed/ISeedKey'
import { IUserKey } from '@/contracts/system-modules/auth/key/IUserKey'
import { IUserKeyStore } from '@/contracts/system-modules/auth/key/IUserKeyStore'
import { IApi } from '@/contracts/core/api/interface/IApi'
import { EnumAuthState } from '@/contracts/system-modules/auth/enum/EnumAuthState'
import { EnumApiError } from '@/contracts/core/api/enum/EnumApiError'
import { IFormEnter } from '@/contracts/system-modules/auth/form/IFormEnter'
import { IFormPins } from '@/contracts/system-modules/auth/form/IFormPins'
import { IFormRegister } from '@/contracts/system-modules/auth/form/IFormRegister'
import { ILoginRequest } from '@/contracts/system-modules/auth/dto/login-request/ILoginRequest'
import { AuthResult } from '@/contracts/system-modules/auth/dto/auth/AuthResult'
import { PinResult, isPinConfirmed } from '@/contracts/system-modules/auth/dto/pin/PinResult'
import { ApiResponse } from '@/contracts/core/api/entity/ApiResponse'
import { SysLockScreenProviderModule } from '@/system-modules/lock-screen/LockScreenProvider'
import { SysSyncProviderModule } from '@/system-modules/sync/SysSyncProvider'
import { SysShareProviderModule } from '@/system-modules/share/SysShareProvider'
import { ApiProviderModule } from '@/core/api/ApiProvider'
import { SeedKey } from '@/system-modules/auth/v1/seed/SeedKey'
import { UserKey } from '@/system-modules/auth/v1/key/UserKey'
import { UserKeyStore } from '@/system-modules/auth/v1/key/UserKeyStore'
import { CreateChallengeRoute } from '@/system-modules/auth/v1/api/v1/auth/CreateChallenge/CreateChallengeRoute'
import { CreateChallengeParams } from '@/system-modules/auth/v1/api/v1/auth/CreateChallenge/Dto/CreateChallengeParams'
import { RegisterRoute } from '@/system-modules/auth/v1/api/v1/auth/Register/RegisterRoute'
import { RegisterParams } from '@/system-modules/auth/v1/api/v1/auth/Register/Dto/RegisterParams'
import { SignedParams } from '@/system-modules/auth/v1/api/v1/auth/Register/Dto/SignedParams'
import { LoginRoute } from '@/system-modules/auth/v1/api/v1/auth/Login/LoginRoute'
import { CheckTokenRoute } from '@/system-modules/auth/v1/api/v1/auth/CheckToken/CheckTokenRoute'
import { LogoutRoute } from '@/system-modules/auth/v1/api/v1/auth/Logout/LogoutRoute'
import { SetPinsRoute } from '@/system-modules/auth/v1/api/v1/auth/SetPins/SetPinsRoute'
import { ChangePinsRoute } from '@/system-modules/auth/v1/api/v1/auth/ChangePins/ChangePinsRoute'
import { ChangePinsParams } from '@/system-modules/auth/v1/api/v1/auth/ChangePins/Dto/ChangePinsParams'
import { IFormChangePins } from '@/contracts/system-modules/auth/form/IFormChangePins'
import { PinsParams } from '@/system-modules/auth/v1/api/v1/auth/SetPins/Dto/PinsParams'
import { PinRoute } from '@/system-modules/auth/v1/api/v1/auth/Pin/PinRoute'
import { PinParams } from '@/system-modules/auth/v1/api/v1/auth/Pin/Dto/PinParams'
import { PinResultRoute } from '@/system-modules/auth/v1/api/v1/auth/PinResult/PinResultRoute'
import { LoginRequestsRoute } from '@/system-modules/auth/v1/api/v1/auth/LoginRequests/LoginRequestsRoute'
import { ConfirmLoginRequestRoute } from '@/system-modules/auth/v1/api/v1/auth/ConfirmLoginRequest/ConfirmLoginRequestRoute'
import { RejectLoginRequestRoute } from '@/system-modules/auth/v1/api/v1/auth/RejectLoginRequest/RejectLoginRequestRoute'
import { readToken, readTokenUserUuid, removeToken, writeToken } from '@/helpers/tokenStorage'
import { readWorkspace, writeWorkspace } from '@/helpers/workspaceStorage'
import { EnumWorkspace, toWorkspace } from '@/contracts/system-modules/auth/enum/EnumWorkspace'
import { EnumSystemEvent } from '@/contracts/core/event/enum/EnumSystemEvent'
import { IEventBus } from '@/contracts/core/event/interface/IEventBus'
import { EventProviderModule } from '@/core/event/EventProvider'

export const HEARTBEAT_INTERVAL_MS = 60000
export const PIN_POLL_MS = 3000

const PIN_PATTERN = /^\d{4}$/
const PIN_VARIANTS = 10000

const MESSAGES = {
  phraseInvalid: 'Сид-фраза неверна: проверьте слова',
  pinFormat: 'PIN — 4 цифры',
  pinsSame: 'PIN входа и PIN сброса должны отличаться',
  deviceNotTrusted: 'Устройство исключено из доверенных: войдите по сид-фразе',
  userNotFound: 'Учётной записи для этой фразы нет',
  userExists: 'Учётная запись для этой фразы уже есть: войдите по ней',
  challengeExpired: 'Вызов устарел, попробуйте ещё раз',
  invalidSignature: 'Подпись не принята: проверьте фразу',
  pinsAlreadySet: 'PIN уже заданы',
  networkError: 'Сервер недоступен',
  loginLocked: 'Вход по фразе временно закрыт',
  loginLockedUntil: 'Вход по фразе закрыт до ',
  pinChangeRequired: 'Вход по фразе закрыт: смените PIN на доверенном устройстве',
  currentPinWrong: 'Текущий PIN входа неверен',
}

const LOGIN_LOCKED_UNTIL_HEADER = 'X-Login-Locked-Until'

interface IFormError {
  errorShow: boolean
  errorMessage: string
}

interface IPins {
  pin: string
  resetPin: string
}

/** Короткий бан — время, бан на сутки и дольше — дата и время */
function formatLockedUntil(date: Date): string {
  const time = date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  if (date.toDateString() === new Date().toDateString()) {
    return time
  }

  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' }) + ' ' + time
}

export class SysAuthV1 implements ISysAuthModule {
  private static instance: ISysAuthModule
  private store: IStore = systemAuthStoreV1()
  private lockScreen: ISysLockScreenModule
  private seedKey: ISeedKey
  private api: IApi
  private sync: () => ISysSyncModule
  private share: () => ISysShareModule
  private events: IEventBus
  private userKeyStore: IUserKeyStore
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private pinPollTimer: ReturnType<typeof setInterval> | null = null
  private pointerOnPage = false
  // фраза шага PIN: только в памяти модуля, не в store и не в хранилище браузера
  private pinStepPhrase: string | null = null
  private pinStepTarget: EnumWorkspace | null = null

  /**
   * Модули sync и share берутся при обращении, а не в конструкторе: auth нужен им обоим,
   * и создание одного из конструктора другого зациклилось бы.
   */
  constructor(
    lockScreen: ISysLockScreenModule = SysLockScreenProviderModule(),
    seedKey: ISeedKey = new SeedKey(),
    api: IApi = ApiProviderModule(),
    sync: () => ISysSyncModule = () => SysSyncProviderModule(),
    share: () => ISysShareModule = () => SysShareProviderModule(),
    events: IEventBus = EventProviderModule(),
    userKeyStore: IUserKeyStore = new UserKeyStore(),
  ) {
    this.lockScreen = lockScreen
    this.seedKey = seedKey
    this.api = api
    this.sync = sync
    this.share = share
    this.events = events
    this.userKeyStore = userKeyStore
  }

  public static getInstance(): ISysAuthModule {
    if (!SysAuthV1.instance) {
      SysAuthV1.instance = new SysAuthV1()
    }
    return SysAuthV1.instance
  }

  init(): boolean {
    this.api.setToken(readToken())
    return true
  }

  isAuth(): boolean {
    return this.store.authorized
  }

  getViewPath(): string {
    return '/auth/v1/view/'
  }

  getStage(): EnumAuthState {
    return this.store.stage
  }

  showLoginForm(): void {
    this.store.stage = EnumAuthState.LOGIN
  }

  showRegisterForm(): void {
    this._endPinStep()
    this.store.stage = EnumAuthState.REGISTER
  }

  cancelPinStep(): void {
    this._endPinStep()
    this.store.formEnter.isRejected = false
    this._setError(this.store.formEnter, '')
  }

  formEnter(): IFormEnter {
    return this.store.formEnter
  }

  formRegister(): IFormRegister {
    return this.store.formRegister
  }

  formPins(): IFormPins {
    return this.store.formPins
  }

  formChangePins(): IFormChangePins {
    return this.store.formChangePins
  }

  showChangePins(isShown: boolean): void {
    Object.assign(this.store.formChangePins, { isShown, currentPin: '', pin: '', resetPin: '' })
    this._setError(this.store.formChangePins, '')
  }

  /** PIN стираются из формы до ответа; третья неудача или PIN сброса исключают устройство */
  async changePins(): Promise<void> {
    const form = this.store.formChangePins
    this._setError(form, '')
    if (!PIN_PATTERN.test(form.currentPin)) {
      return this._setError(form, MESSAGES.pinFormat)
    }
    if (!this._arePinsValid(form, form)) {
      return
    }

    const params = new ChangePinsParams({ currentPin: form.currentPin, pin: form.pin, resetPin: form.resetPin })
    Object.assign(form, { currentPin: '', pin: '', resetPin: '' })
    const response = await new ChangePinsRoute().exec(params)
    if (response.isSuccess()) {
      return this.showChangePins(false)
    }
    if (response.getError() === EnumApiError.DEVICE_NOT_TRUSTED) {
      return this._dropSession(MESSAGES.deviceNotTrusted)
    }

    this._setError(form, response.getError() === EnumApiError.INVALID_PIN ? MESSAGES.currentPinWrong : this._message(response.getError()))
  }

  getUserName(): string {
    return this.store.user.name
  }

  getDeviceUuid(): string {
    return this.store.user.deviceUuid
  }

  getUserUuid(): string {
    return this.store.user.uuid
  }

  getUserKey(): IUserKey | null {
    return this.store.userKey ? new UserKey(this.store.userKey) : null
  }

  getLoginRequests(): ILoginRequest[] {
    return this.store.loginRequests
  }

  getWorkspace(): EnumWorkspace {
    return this.store.workspace
  }

  getWorkspaceSwitch(): EnumWorkspace | null {
    return this.store.workspaceSwitch
  }

  /** Закрытие окна прекращает ожидание подтверждения */
  showWorkspaceSwitch(workspace: EnumWorkspace | null): void {
    this._endPinStep()
    this.store.workspaceSwitch = workspace
    this.store.formEnter.input = ''
    this.store.formEnter.isWaiting = false
    this._setError(this.store.formEnter, '')
    if (workspace === null) {
      this._stopPinPoll()
    }
  }

  /** Как вход через одно поле, но с целью окна: PIN — запрос переключения, фраза — вход в это рабочее место */
  async switchWorkspace(input: string): Promise<void> {
    const target = this.store.workspaceSwitch
    const form = this.store.formEnter
    const value = input.trim()
    form.input = ''
    this._setError(form, '')
    if (target === null) {
      return
    }

    if (form.isPinStep) {
      return this._enterPhrasePin(value)
    }

    if (PIN_PATTERN.test(value)) {
      return this._requestSwitch(value, target)
    }

    return this._switchByPhrase(value, target)
  }

  createPhrase(): string {
    const form = this.store.formRegister
    form.phrase = this.seedKey.createPhrase()
    // PIN задаёт система: придуманные людьми PIN легко подобрать
    form.pin = this._createPin()
    do {
      form.resetPin = this._createPin()
    } while (form.resetPin === form.pin)
    this._setError(form, '')

    return form.phrase
  }

  async register(): Promise<void> {
    const form = this.store.formRegister
    this._setError(form, '')
    if (!this._arePinsValid(form, form)) {
      return
    }

    const phrase = form.phrase
    const signed = await this._signedParams(phrase)
    const response = await new RegisterRoute().exec(new RegisterParams({ ...signed, pin: form.pin, resetPin: form.resetPin, workspace: readWorkspace() }))
    this._forgetSecrets(form)

    await this._finishLogin(response, form, phrase)
  }

  /** Четыре цифры — PIN, иначе — сид-фраза; поле стирается в обоих случаях */
  async enter(input: string): Promise<void> {
    const form = this.store.formEnter
    const value = input.trim()
    form.input = ''
    form.isRejected = false
    this._setError(form, '')

    if (form.isPinStep) {
      return this._enterPhrasePin(value)
    }

    if (PIN_PATTERN.test(value)) {
      return this._enterPin(value)
    }

    return this._enterPhrase(value, form)
  }

  async setPins(): Promise<void> {
    const form = this.store.formPins
    this._setError(form, '')
    if (!this._arePinsValid(form, form)) {
      return
    }

    const response = await new SetPinsRoute().exec(new PinsParams({ pin: form.pin, resetPin: form.resetPin }))
    form.pin = ''
    form.resetPin = ''
    if (!response.isSuccess() && response.getError() !== 'pins_already_set') {
      this._setError(form, this._message(response.getError()))
      return
    }

    this._startSession()
  }

  async restoreSession(): Promise<void> {
    const token = readToken()
    if (!token) {
      return
    }
    this.api.setToken(token)

    const response = await new CheckTokenRoute().exec(null)
    if (response.isSuccess()) {
      const state = response.getData()
      this._setToken(state.token)
      this._setUser(state, state.deviceUuid)
      this._setWorkspace(state.workspace)
      await this._loadUserKey()
      this.sync().start()
      this.events.emit(EnumSystemEvent.UNLOCKED)
      return
    }

    if (response.getError() === EnumApiError.UNAUTHORIZED) {
      // токен просрочен, устройство доверенное: экран блокируется, токен остаётся для входа по PIN
      this.store.authorized = true
      this.lockScreen.lock()
      return
    }

    if (this._needsPhrase(response.getError())) {
      await this._dropSession(MESSAGES.deviceNotTrusted)
      return
    }

    this._setError(this.store.formEnter, this._message(response.getError()))
  }

  async logout(): Promise<void> {
    this._stopTimer()
    this._stopPinPoll()
    if (readToken()) {
      await new LogoutRoute().exec(null)
    }
    await this._dropSession('')
  }

  async confirmLogin(uuid: string): Promise<void> {
    await new ConfirmLoginRequestRoute(uuid).exec(null)
    this._forgetLoginRequest(uuid)
  }

  async rejectLogin(uuid: string): Promise<void> {
    await new RejectLoginRequestRoute(uuid).exec(null)
    this._forgetLoginRequest(uuid)
  }

  startHeartbeat(): void {
    this.pointerOnPage = true
    this._resumeHeartbeat()
  }

  stopHeartbeat(): void {
    this.pointerOnPage = false
    this._stopTimer()
  }

  /** PIN: при токене — запрос узлу и опрос результата; без токена — только экран ожидания */
  async _enterPin(pin: string): Promise<void> {
    const form = this.store.formEnter
    form.isWaiting = true
    if (!readToken()) {
      return
    }

    const response = await new PinRoute().exec(new PinParams({ pin }))
    if (!response.isSuccess() || !form.isWaiting) {
      return
    }

    this._startPinPoll(response.getData().request)
  }

  async _enterPhrase(phrase: string, form: IFormEnter): Promise<void> {
    this._stopPinPoll()
    form.isWaiting = false
    if (!this.seedKey.isPhraseValid(phrase)) {
      form.isRejected = true
      return
    }

    const response = await new LoginRoute().exec(await this._signedParams(phrase, readWorkspace()))
    if (this._isPinRequired(response)) {
      return this._startPinStep(phrase, null)
    }

    await this._finishLogin(response, form, phrase)
  }

  /** Фраза принята, узел просит PIN входа: без него устройство не создаётся */
  _startPinStep(phrase: string, target: EnumWorkspace | null): void {
    this.pinStepPhrase = phrase
    this.pinStepTarget = target
    this.store.formEnter.isPinStep = true
  }

  _endPinStep(): void {
    this.pinStepPhrase = null
    this.pinStepTarget = null
    this.store.formEnter.isPinStep = false
  }

  /** Неверный PIN оставляет шаг: звёзды падают, можно ввести снова, пока узел не закроет вход баном */
  async _enterPhrasePin(pin: string): Promise<void> {
    const form = this.store.formEnter
    const phrase = this.pinStepPhrase
    const target = this.pinStepTarget
    if (phrase === null || !PIN_PATTERN.test(pin)) {
      form.isRejected = true
      return
    }

    const response = await new LoginRoute().exec(await this._signedParams(phrase, target ?? readWorkspace(), pin))
    if (!response.isSuccess() && response.getError() === EnumApiError.INVALID_PIN) {
      form.isRejected = true
      return
    }

    this._endPinStep()
    if (!response.isSuccess()) {
      return this._setError(form, this._loginError(response))
    }
    if (target === null) {
      return this._finishLogin(response, form, phrase)
    }

    this._applyAuthResult(response.getData())
    await this._saveUserKey(phrase)
    this._finishSwitch()
  }

  _isPinRequired(response: ApiResponse<AuthResult>): boolean {
    return !response.isSuccess() && response.getError() === EnumApiError.PIN_REQUIRED
  }

  /** Бан входа по фразе: время окончания приходит заголовком, тело ошибки по стандарту пустое */
  _loginError(response: ApiResponse<AuthResult>): string {
    if (response.getError() !== EnumApiError.LOGIN_LOCKED) {
      return this._message(response.getError())
    }
    const until = response.getHeader(LOGIN_LOCKED_UNTIL_HEADER)
    const date = until ? new Date(until) : null
    if (!date || isNaN(date.getTime())) {
      return MESSAGES.loginLocked
    }

    return MESSAGES.loginLockedUntil + formatLockedUntil(date)
  }

  async _requestSwitch(pin: string, target: EnumWorkspace): Promise<void> {
    this.store.formEnter.isWaiting = true
    if (!readToken()) {
      return
    }

    const response = await new PinRoute().exec(new PinParams({ pin, workspace: target }))
    if (!response.isSuccess() || !this.store.formEnter.isWaiting) {
      return
    }

    this._startPinPoll(response.getData().request)
  }

  /** Сид-фраза в окне переключения — вход по фразе: устройство получает новую запись в выбранном рабочем месте */
  async _switchByPhrase(phrase: string, target: EnumWorkspace): Promise<void> {
    const form = this.store.formEnter
    this._stopPinPoll()
    form.isWaiting = false
    if (!this.seedKey.isPhraseValid(phrase)) {
      return this._setError(form, MESSAGES.phraseInvalid)
    }

    const response = await new LoginRoute().exec(await this._signedParams(phrase, target))
    if (this._isPinRequired(response)) {
      return this._startPinStep(phrase, target)
    }
    if (!response.isSuccess()) {
      return this._setError(form, this._loginError(response))
    }

    this._applyAuthResult(response.getData())
    await this._saveUserKey(phrase)
    this._finishSwitch()
  }

  /** Переключение подтверждено: окно закрыто, система открывает модуль нового рабочего места */
  _finishSwitch(): void {
    this.showWorkspaceSwitch(null)
    this.events.emit(EnumSystemEvent.WORKSPACE_CHANGED)
  }

  _startPinPoll(request: string): void {
    this._stopPinPoll()
    this.pinPollTimer = setInterval(() => this._pollPinResult(request), PIN_POLL_MS)
    this._pollPinResult(request)
  }

  _stopPinPoll(): void {
    if (this.pinPollTimer) {
      clearInterval(this.pinPollTimer)
      this.pinPollTimer = null
    }
  }

  /** Любой ответ, кроме подтверждённого, ничего не меняет: ожидание продолжается */
  async _pollPinResult(request: string): Promise<void> {
    const response = await new PinResultRoute(request).exec(null)
    if (!response.isSuccess() || !this.pinPollTimer) {
      return
    }
    const result = response.getData()
    if (!isPinConfirmed(result)) {
      return
    }

    this._stopPinPoll()
    this._applyAuthResult(result)
    await this._loadUserKey()
    if (this.store.workspaceSwitch !== null) {
      this._finishSwitch()
      return
    }

    this.store.formEnter.isWaiting = false
    // окно блокировки само объявляет unlocked; вход с экрана входа объявляет его в _startSession
    const wasLocked = this.lockScreen.isLocked()
    if (wasLocked) {
      this.lockScreen.unlock()
    }
    this._startSession(!wasLocked)
  }

  _resumeHeartbeat(): void {
    if (!this.pointerOnPage || this.heartbeatTimer || !this._isWorking()) {
      return
    }
    this.heartbeatTimer = setInterval(() => this._beat(), HEARTBEAT_INTERVAL_MS)
    this._beat()
  }

  _stopTimer(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  /** Heartbeat идёт только у вошедшего пользователя и не идёт, пока экран заблокирован */
  _isWorking(): boolean {
    return this.store.authorized && !this.lockScreen.isLocked()
  }

  async _beat(): Promise<void> {
    const response = await new CheckTokenRoute().exec(null)
    if (response.isSuccess()) {
      this._setToken(response.getData().token)
      this._setWorkspace(response.getData().workspace)
      await this._exchangeOperations()
      await this._receiveLoginRequests()
      return
    }

    if (response.getError() === EnumApiError.UNAUTHORIZED) {
      this._stopTimer()
      this.lockScreen.lock()
      return
    }

    if (this._needsPhrase(response.getError())) {
      this._stopTimer()
      await this._dropSession(MESSAGES.deviceNotTrusted)
    }
  }

  /** Обмен операциями идёт вместе с heartbeat: сначала очередь уходит на узел, затем приходят новые */
  async _exchangeOperations(): Promise<void> {
    await this.sync().send()
    await this.sync().receive()
  }

  /** Запросы входа с других устройств приходят вместе с heartbeat */
  async _receiveLoginRequests(): Promise<void> {
    const response = await new LoginRequestsRoute().exec(null)
    if (response.isSuccess()) {
      this.store.loginRequests = response.getData()
    }
  }

  _forgetLoginRequest(uuid: string): void {
    this.store.loginRequests = this.store.loginRequests.filter((request) => request.uuid !== uuid)
  }

  async _signedParams(phrase: string, workspace?: EnumWorkspace, pin?: string): Promise<SignedParams> {
    const publicKey = await this.seedKey.getPublicKey(phrase)
    const challenge = await new CreateChallengeRoute().exec(new CreateChallengeParams({ publicKey }))
    if (!challenge.isSuccess()) {
      return new SignedParams({ publicKey, challenge: '', signature: '', workspace, pin })
    }

    return new SignedParams({
      publicKey,
      challenge: challenge.getData().challenge,
      signature: await this.seedKey.signChallenge(phrase, challenge.getData().challenge),
      workspace,
      pin,
    })
  }

  /** Ключ пользователя сохраняется до объявления о входе: модули узнают о входе уже с ключом */
  async _finishLogin(response: ApiResponse<AuthResult>, form: IFormError, phrase: string): Promise<void> {
    if (!response.isSuccess()) {
      this._setError(form, this._message(response.getError()))
      return
    }

    const result = response.getData()
    this._applyAuthResult(result)
    await this._saveUserKey(phrase)
    this.lockScreen.hide()
    this.store.formEnter.isWaiting = false
    if (result.hasPins === false) {
      this.store.stage = EnumAuthState.PINS
      return
    }

    this._startSession()
  }

  /** Вход завершён: экран входа убран, обмен и heartbeat запущены, модули узнают о входе событием unlocked */
  _startSession(announce = true): void {
    this.store.stage = EnumAuthState.LOGIN
    this.sync().start()
    this._resumeHeartbeat()
    if (announce) {
      this.events.emit(EnumSystemEvent.UNLOCKED)
    }
  }

  _applyAuthResult(result: AuthResult | PinResult): void {
    if (typeof result.token !== 'string' || typeof result.deviceUuid !== 'string' || !result.user) {
      return
    }
    this._setToken(result.token)
    this._setUser(result.user, result.deviceUuid)
    this._setWorkspace(result.workspace)
  }

  /** Значение узла — источник правды; браузер запоминает его для входа по фразе. Ответ — простой объект: поля может не быть */
  _setWorkspace(value: unknown): void {
    const workspace = toWorkspace(value)
    this.store.workspace = workspace
    writeWorkspace(workspace)
  }

  _setUser(user: { uuid: string, name: string }, deviceUuid: string): void {
    this.store.user.uuid = user.uuid
    this.store.user.name = user.name
    this.store.user.deviceUuid = deviceUuid
    this.store.authorized = true
  }

  _setToken(token: string): void {
    writeToken(token)
    this.api.setToken(token)
  }

  /** Устройство больше не доверенное: ключ и токен удаляются, дальше только вход по фразе */
  async _dropSession(message: string): Promise<void> {
    this._stopTimer()
    this._stopPinPoll()
    this.sync().stop()
    this.share().leaveViewer()
    await this._removeUserKey()
    removeToken()
    this.api.setToken(null)
    this._endPinStep()
    this.store.reset()
    this.lockScreen.hide()
    this._setError(this.store.formEnter, message)
  }

  /** Вход по фразе: ключ выводится из фразы и ложится на устройство; фраза в памяти не задерживается */
  async _saveUserKey(phrase: string): Promise<void> {
    if (!this.store.user.uuid) {
      return
    }
    try {
      const key = await this.seedKey.getUserKey(phrase)
      await this.userKeyStore.save(this.store.user.uuid, key)
      this.store.userKey = markRaw(key)
    } catch (e) {
      console.error('auth: user key is not saved', e)
      this.store.userKey = null
    }
  }

  /** Запуск, перезагрузка, вход по PIN: ключ с устройства; нет ключа — нужен вход по фразе */
  async _loadUserKey(): Promise<void> {
    if (!this.store.user.uuid) {
      return
    }
    try {
      const key = await this.userKeyStore.load(this.store.user.uuid)
      this.store.userKey = key ? markRaw(key) : null
    } catch (e) {
      console.error('auth: user key is not loaded', e)
      this.store.userKey = null
    }
  }

  /**
   * UUID учётной записи берётся из store, а после перезагрузки с отклонённым токеном —
   * из самого токена: иначе ключ исключённого устройства остался бы на нём
   */
  async _removeUserKey(): Promise<void> {
    const userUuid = this.store.user.uuid || readTokenUserUuid()
    this.store.userKey = null
    if (!userUuid) {
      return
    }
    try {
      await this.userKeyStore.remove(userUuid)
    } catch (e) {
      console.error('auth: user key is not removed', e)
    }
  }

  _createPin(): string {
    const value = crypto.getRandomValues(new Uint32Array(1))[0] % PIN_VARIANTS

    return String(value).padStart(4, '0')
  }

  _forgetSecrets(form: IFormRegister): void {
    form.phrase = ''
    form.pin = ''
    form.resetPin = ''
  }

  _arePinsValid(form: IFormError, pins: IPins): boolean {
    if (!PIN_PATTERN.test(pins.pin) || !PIN_PATTERN.test(pins.resetPin)) {
      this._setError(form, MESSAGES.pinFormat)
      return false
    }
    if (pins.pin === pins.resetPin) {
      this._setError(form, MESSAGES.pinsSame)
      return false
    }

    return true
  }

  _needsPhrase(error: string): boolean {
    return error === EnumApiError.DEVICE_NOT_TRUSTED || error === 'user_not_found'
  }

  _setError(form: IFormError, message: string): void {
    form.errorShow = message !== ''
    form.errorMessage = message
  }

  _message(error: string): string {
    const known: Record<string, string> = {
      [EnumApiError.DEVICE_NOT_TRUSTED]: MESSAGES.deviceNotTrusted,
      [EnumApiError.NETWORK_ERROR]: MESSAGES.networkError,
      user_not_found: MESSAGES.userNotFound,
      user_already_exists: MESSAGES.userExists,
      challenge_expired: MESSAGES.challengeExpired,
      [EnumApiError.LOGIN_LOCKED]: MESSAGES.loginLocked,
      [EnumApiError.PIN_CHANGE_REQUIRED]: MESSAGES.pinChangeRequired,
      invalid_signature: MESSAGES.invalidSignature,
      invalid_pin: MESSAGES.pinsSame,
      pins_already_set: MESSAGES.pinsAlreadySet,
      validation_failed: MESSAGES.pinsSame,
    }

    return known[error] ?? error
  }
}

export default function (): ISysAuthModule {
  return SysAuthV1.getInstance()
}
